import { useMutation } from '@apollo/client/react'
import { toast } from '@buffer-mono/popcorn'

import { useUser } from '../../../../../common/context/User'
import type { Channel } from '../../../../../common/types'
import { getChannelFromAccount } from '../../../../../common/utils/channels'
import { UPDATE_CHANNEL_POSTING_GOAL_MUTATION } from '../mutations'

interface UsePostingGoalMutationArgs {
  channelIds?: string[]
  overridePostingSchedules?: boolean
}

interface UsePostingGoalMutationResult {
  channels: Channel[]
  channel: Channel | undefined
  loading: boolean
  error: unknown
  createPostingGoal: (goal: number) => Promise<boolean>
}

function getChannelFromUrl(): string | null {
  const pathname = window.location.pathname
  return pathname.match(/\/channels\/([^/]+)/)?.[1] ?? null
}

export function usePostingGoalMutation({
  channelIds: passedChannelIds,
  overridePostingSchedules = true,
}: UsePostingGoalMutationArgs): UsePostingGoalMutationResult {
  const account = useUser()
  const [updatePostingGoal, { loading, error }] = useMutation(
    UPDATE_CHANNEL_POSTING_GOAL_MUTATION,
    {
      refetchQueries: [
        'GetChannelInfo',
        'GetPostingScheduleInfo',
        'checklists',
      ],
    },
  )

  const channelIds =
    passedChannelIds ??
    [getChannelFromUrl()].filter((x): x is string => Boolean(x))

  const channels = channelIds.map((channelId) =>
    getChannelFromAccount(account, channelId),
  )
  const channel = channels?.[0]

  const createPostingGoal = async (goal: number): Promise<boolean> => {
    const failedChannels: Channel[] = []

    for (const channel of channels) {
      try {
        const result = await updatePostingGoal({
          variables: {
            input: {
              channelId: channel.id,
              goal,
              overridePostingSchedules,
            },
          },
        })
        if (
          result.data?.updateChannelPostingGoal.__typename !==
          'UpdateChannelPostingGoalResponse'
        ) {
          failedChannels.push(channel)
        }
      } catch {
        failedChannels.push(channel)
      }
    }

    if (failedChannels.length === 1) {
      toast.error(
        `Failed to create posting goal for ${
          failedChannels[0]?.name || 'a channel'
        }.`,
      )
    } else if (failedChannels.length > 1) {
      toast.error(
        `Failed to create posting goals for ${failedChannels.length} channels.`,
      )
    } else {
      toast.success('Posting goal created successfully.')
    }

    return failedChannels.length === 0
  }

  return {
    channels,
    channel,
    loading,
    error,
    createPostingGoal,
  }
}
